// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { theme } from "@insite/client-framework/Theme";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getEventData, getIsEvent } from "@mcmc/App/Store/Selectors/ProductDetailsSelectors";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProduct;

const mapStateToProps = (state: McMcApplicationState, props: OwnProps) => ({
    eventData: getEventData(state, props.product),
    isEvent: getIsEvent(state, props.product),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsPageTitleStyles {
    container?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    titleText?: TypographyProps;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 90px;
                }
            `,
        },
        nameText: {
            size: 16,
        },
        nameLink: {
            typographyProps: {
                size: 16,
            },
            css: css`
                ${getProp("theme.link.defaultProps.css")}
            `,
        },
    },
    titleText: {
        variant: "h2",
        forwardAs: "h1",
        size: 30,
        lineHeight: "35px",
        css: css`
            letter-spacing: 0.4px;
            margin-bottom: 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            line-height: 28px;
                            font-size: 24px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product, isEvent, eventData }) => {
    let productTitle;
    if (isEvent) {
        productTitle = product.productTitle;
        if (eventData && eventData.category) {
            productTitle = product.productTitle + (eventData.category ? ` (${product.properties.eventCategory})` : "");
        }
    } else {
        productTitle = product.manufacturerItem || product.productTitle;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.titleText} data-test-selector="productDetails_productDescription">{productTitle}</Typography>
            {product.brand && <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsPageTitle)),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
